// Catalog.ID Client — Trust Received Pane
// ============================================================================
// Shows incoming trust summary: count, distribution, and anonymized list.

import { createElement, createFieldGroup, createFormRow, createEmptyState } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import * as trustApi from '../api/trust';
import { identity } from '../state/identity';
import { PANE_IDS } from '../shell-config';

const SCORE_LABELS: Record<number, string> = {
    1: 'Distrust',
    2: 'Low',
    3: 'Neutral',
    4: 'Trust',
    5: 'High Trust',
};

const SCORE_BAR_COLORS: Record<number, string> = {
    1: '#e44',
    2: '#e90',
    3: '#6af',
    4: '#6c6',
    5: '#2d2',
};

export function createTrustReceivedPane(): PaneType {
    return {
        typeId: PANE_IDS.trustReceived,
        kind: 'leaf',
        defaultTitle: 'Trust Received',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderReceived(container, ctx);
            return container;
        },
    };
}

async function renderReceived(container: HTMLElement, _ctx: PaneTypeRenderContext): Promise<void> {
    const active = identity.getActive();
    if (!active) {
        container.appendChild(createEmptyState({ message: 'Not logged in' }));
        return;
    }

    // Load trust score and received data in parallel
    const loadingEl = createElement('span', { className: 'cid-wizard-status', textContent: 'Loading...' });
    container.appendChild(loadingEl);

    try {
        const [score, received] = await Promise.all([
            trustApi.getTrustScore(active.cid),
            trustApi.getReceivedTrust(),
        ]);

        container.removeChild(loadingEl);

        // Score summary
        const scoreGroup = createFieldGroup({ title: 'Your Trust Score' });

        const scoreRow = createFormRow({ label: 'Global Score' });
        scoreRow.content.appendChild(createElement('span', {
            className: 'cid-trust-score-value',
            textContent: score.score.toFixed(4),
        }));
        scoreGroup.body.appendChild(scoreRow.element);

        const tierRow = createFormRow({ label: 'Tier' });
        tierRow.content.appendChild(createElement('span', {
            className: 'cid-trust-tier-badge',
            textContent: `Tier ${score.tier}`,
        }));
        tierRow.content.firstElementChild?.setAttribute('data-tier', String(score.tier));
        scoreGroup.body.appendChild(tierRow.element);

        const inRow = createFormRow({ label: 'Incoming Ratings' });
        inRow.content.appendChild(createElement('span', { textContent: String(score.incomingCount) }));
        scoreGroup.body.appendChild(inRow.element);

        const outRow = createFormRow({ label: 'Outgoing Ratings' });
        outRow.content.appendChild(createElement('span', { textContent: String(score.outgoingCount) }));
        scoreGroup.body.appendChild(outRow.element);

        container.appendChild(scoreGroup.element);

        // Distribution
        const distGroup = createFieldGroup({ title: 'Rating Distribution' });

        if (received.count === 0) {
            distGroup.body.appendChild(createElement('span', {
                className: 'cid-wizard-status',
                textContent: 'No ratings received yet',
            }));
        } else {
            const distContainer = createElement('div', { className: 'cid-trust-distribution' });
            const maxCount = Math.max(...Object.values(received.distribution), 1);

            for (let s = 5; s >= 1; s--) {
                const count = received.distribution[s] ?? 0;
                const row = createElement('div', { className: 'cid-trust-dist-row' });

                row.appendChild(createElement('span', {
                    className: 'cid-trust-dist-label',
                    textContent: SCORE_LABELS[s],
                }));

                const barOuter = createElement('div', { className: 'cid-trust-dist-bar-outer' });
                const barInner = createElement('div', { className: 'cid-trust-dist-bar-inner' });
                barInner.style.width = `${(count / maxCount) * 100}%`;
                barInner.style.backgroundColor = SCORE_BAR_COLORS[s];
                barOuter.appendChild(barInner);
                row.appendChild(barOuter);

                row.appendChild(createElement('span', {
                    className: 'cid-trust-dist-count',
                    textContent: String(count),
                }));

                distContainer.appendChild(row);
            }
            distGroup.body.appendChild(distContainer);
        }

        container.appendChild(distGroup.element);

    } catch {
        container.removeChild(loadingEl);
        container.appendChild(createElement('span', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: 'Failed to load trust data',
        }));
    }
}
